import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Wine, Users, ClipboardList, Trophy, ArrowRight, Swords, Crown, Plus, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TournamentSection } from "@/components/TournamentSection";
import ContinueStudyingCard from "@/components/ContinueStudyingCard";
import AccountSection from "@/components/AccountSection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [orphanCocktails, setOrphanCocktails] = useState(0);
  const [orphanLibrary, setOrphanLibrary] = useState(0);
  const [adopting, setAdopting] = useState(false);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [statsRes, userRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/auth/me`).catch(() => ({ data: null })),
      ]);
      setStats(statsRes.data);
      setCurrentUser(userRes.data);
      // Check for orphaned content (admin-only endpoint, fail silently for staff)
      if (userRes.data && ["admin", "org_admin", "system_admin"].includes(userRes.data.role)) {
        try {
          const [cInv, lInv] = await Promise.all([
            axios.get(`${API}/admin/cocktails/inventory`),
            axios.get(`${API}/admin/library/inventory`),
          ]);
          setOrphanCocktails(cInv.data?.orphan_total || 0);
          setOrphanLibrary(lInv.data?.orphan_total || 0);
        } catch { /* non-admin or no org — ignore */ }
      }
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const orphanTotal = orphanCocktails + orphanLibrary;

  const handleAdoptOrphans = async () => {
    const parts = [];
    if (orphanCocktails) parts.push(`${orphanCocktails} cocktail${orphanCocktails === 1 ? "" : "s"}`);
    if (orphanLibrary) parts.push(`${orphanLibrary} library item${orphanLibrary === 1 ? "" : "s"}`);
    if (!window.confirm(`Reassign ${parts.join(" and ")} to your organisation? They will become private to your org.`)) return;
    setAdopting(true);
    try {
      let adopted = 0;
      let targetName = "your organisation";
      if (orphanCocktails) {
        const res = await axios.post(`${API}/admin/cocktails/adopt-orphans`);
        adopted += res.data.adopted || 0;
        targetName = res.data.target_org_name || targetName;
      }
      if (orphanLibrary) {
        const res = await axios.post(`${API}/admin/library/adopt-orphans`);
        adopted += res.data.adopted || 0;
        targetName = res.data.target_org_name || targetName;
      }
      toast.success(`Adopted ${adopted} item${adopted === 1 ? "" : "s"} into ${targetName}`);
      setOrphanCocktails(0);
      setOrphanLibrary(0);
      fetchStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to adopt orphans");
    } finally {
      setAdopting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="dashboard-loading">
        <div className="text-[hsl(var(--muted-foreground))]">Loading...</div>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page" className="space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-[hsl(var(--muted-foreground))] mt-2">
            Overview of your cocktail testing system
          </p>
        </div>
        <Link to="/create-test">
          <Button 
            className="btn-glow bg-[hsl(var(--primary))] text-black hover:bg-[hsl(var(--primary))]/90"
            data-testid="create-test-btn"
          >
            Create New Test
            <ArrowRight className="ml-2" size={18} />
          </Button>
        </Link>
      </div>

      {/* Orphaned-content banner (admin only, when drift is detected) */}
      {orphanTotal > 0 && (
        <div
          className="flex items-start gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3"
          data-testid="orphan-banner"
        >
          <AlertTriangle className="text-amber-400 shrink-0 mt-0.5" size={20} />
          <div className="flex-1">
            <p className="text-sm font-semibold text-amber-100">
              {orphanCocktails > 0 && (
                <>{orphanCocktails} orphaned cocktail{orphanCocktails === 1 ? "" : "s"}</>
              )}
              {orphanCocktails > 0 && orphanLibrary > 0 && " · "}
              {orphanLibrary > 0 && (
                <>{orphanLibrary} orphaned library item{orphanLibrary === 1 ? "" : "s"}</>
              )}
              {" detected"}
            </p>
            <p className="text-xs text-amber-100/70 mt-0.5">
              These items aren't assigned to any active organisation, so nobody on your team can see them. Click below to claim them for your organisation.
            </p>
          </div>
          <Button
            size="sm"
            onClick={handleAdoptOrphans}
            disabled={adopting}
            className="bg-amber-500 text-black hover:bg-amber-400"
            data-testid="adopt-orphans-btn"
          >
            {adopting ? "Adopting..." : `Adopt ${orphanTotal}`}
          </Button>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="stat-card hover-lift" data-testid="stat-cocktails">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-[hsl(var(--primary))]/10">
              <Wine className="text-[hsl(var(--primary))]" size={24} />
            </div>
            <div>
              <p className="stat-label">Cocktails</p>
              <p className="stat-value">{stats?.cocktail_count || 0}</p>
            </div>
          </div>
        </div>

        <div className="stat-card hover-lift" data-testid="stat-staff">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-[hsl(var(--primary))]/10">
              <Users className="text-[hsl(var(--primary))]" size={24} />
            </div>
            <div>
              <p className="stat-label">Staff Members</p>
              <p className="stat-value">{stats?.staff_count || 0}</p>
            </div>
          </div>
        </div>

        <div className="stat-card hover-lift" data-testid="stat-tests">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-[hsl(var(--primary))]/10">
              <ClipboardList className="text-[hsl(var(--primary))]" size={24} />
            </div>
            <div>
              <p className="stat-label">Saved Tests</p>
              <p className="stat-value">{stats?.test_count || 0}</p>
            </div>
          </div>
        </div>

        <div className="stat-card hover-lift" data-testid="stat-avg-score">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-[hsl(var(--primary))]/10">
              <Trophy className="text-[hsl(var(--primary))]" size={24} />
            </div>
            <div>
              <p className="stat-label">Avg Score</p>
              <p className="stat-value">{stats?.avg_score || 0}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions & Recent Results */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Quick Actions */}
        <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
          <CardHeader>
            <CardTitle className="text-xl">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Link to="/upload" className="block">
              <div 
                className="p-4 rounded-lg bg-[hsl(var(--secondary))] hover:bg-[hsl(var(--accent))] transition-colors cursor-pointer"
                data-testid="quick-action-upload"
              >
                <p className="font-medium">Upload Cocktail Specs</p>
                <p className="text-sm text-[hsl(var(--muted-foreground))]">
                  Import from CSV file
                </p>
              </div>
            </Link>
            <Link to="/staff" className="block">
              <div 
                className="p-4 rounded-lg bg-[hsl(var(--secondary))] hover:bg-[hsl(var(--accent))] transition-colors cursor-pointer"
                data-testid="quick-action-staff"
              >
                <p className="font-medium">Manage Staff</p>
                <p className="text-sm text-[hsl(var(--muted-foreground))]">
                  Add or remove team members
                </p>
              </div>
            </Link>
            <Link to="/cocktails" className="block">
              <div 
                className="p-4 rounded-lg bg-[hsl(var(--secondary))] hover:bg-[hsl(var(--accent))] transition-colors cursor-pointer"
                data-testid="quick-action-browse"
              >
                <p className="font-medium">Browse Cocktails</p>
                <p className="text-sm text-[hsl(var(--muted-foreground))]">
                  View and filter all specs
                </p>
              </div>
            </Link>
          </CardContent>
        </Card>

        {/* Recent Results */}
        <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
          <CardHeader>
            <CardTitle className="text-xl">Recent Test Results</CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.recent_results && stats.recent_results.length > 0 ? (
              <div className="space-y-3">
                {stats.recent_results.map((result, idx) => (
                  <div 
                    key={idx}
                    className="flex items-center justify-between p-3 rounded-lg bg-[hsl(var(--secondary))]"
                    data-testid={`recent-result-${idx}`}
                  >
                    <div>
                      <p className="font-medium">{result.staff_name}</p>
                      <p className="text-sm text-[hsl(var(--muted-foreground))]">
                        {new Date(result.completed_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className={`text-lg font-bold ${
                      result.percentage >= 80 
                        ? 'text-green-500' 
                        : result.percentage >= 60 
                        ? 'text-[#C36A2F]' 
                        : 'text-red-500'
                    }`}>
                      {result.percentage.toFixed(0)}%
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[hsl(var(--muted-foreground))] text-center py-8">
                No test results yet
              </p>
            )}
            {stats?.recent_results && stats.recent_results.length > 0 && (
              <Link to="/history" className="block mt-4">
                <Button variant="outline" className="w-full" data-testid="view-all-results-btn">
                  View All Results
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Continue Studying (shared with staff dashboard) */}
      <ContinueStudyingCard />

      {/* Tournaments */}
      <TournamentSection currentUser={currentUser} />

      {/* Account */}
      <AccountSection user={currentUser} />
    </div>
  );
}
